import { App } from '@capacitor/app';
import { Capacitor, type PluginListenerHandle } from '@capacitor/core';

export interface ProactiveVisibility {
  readonly native: boolean;
  isVisible(): boolean;
  dispose(): void;
}

/** Native foreground confirmation and DOM visibility must both permit scheduled work. */
export function observeProactiveVisibility(
  changed: (reason: 'return' | 'failure') => void,
): ProactiveVisibility {
  const native = Capacitor.isNativePlatform();
  let alive = true, registered = false, registrationFailed = false;
  let nativeActive: boolean | undefined, eventRevision = 0;
  let listener: PluginListenerHandle | undefined;
  let setupTimer: ReturnType<typeof setTimeout> | undefined;
  const confirm = () => {
    if (registered && nativeActive !== undefined) clearTimeout(setupTimer);
  };
  const isVisible = () => alive && document.visibilityState === 'visible' &&
    (!native || (registered && !registrationFailed && nativeActive === true));
  const returned = () => { if (alive) changed('return'); };
  document.addEventListener('visibilitychange', returned);
  window.addEventListener('focus', returned);
  window.addEventListener('online', returned);
  const remove = async (handle: PluginListenerHandle) => {
    try { await handle.remove(); }
    catch { console.warn('[proactive] Native lifecycle listener cleanup failed'); }
  };
  if (native) {
    setupTimer = setTimeout(() => {
      if (!alive) return;
      console.warn('[proactive] Native foreground setup could not be confirmed');
      changed('failure');
    }, 12_000);
    // Promise normalization also catches a synchronous bridge failure.
    void Promise.resolve().then(() => App.addListener('appStateChange', ({ isActive }) => {
      if (!alive || registrationFailed) return;
      eventRevision++;
      nativeActive = isActive;
      confirm();
      if (registered) changed('return');
    })).then((handle) => {
      if (!alive) { void remove(handle); return; }
      listener = handle;
      registered = true;
      confirm();
      if (nativeActive !== undefined) changed('return');
      void Promise.resolve().then(() => App.getState()).then(({ isActive }) => {
        // An event received during setup is newer than the initial snapshot.
        if (!alive || eventRevision !== 0) return;
        nativeActive = isActive;
        confirm();
        changed('return');
      }).catch(() => {
        if (!alive || eventRevision !== 0) return;
        nativeActive = undefined;
        clearTimeout(setupTimer);
        console.warn('[proactive] Native foreground state could not be confirmed');
        changed('failure');
      });
    }).catch(() => {
      if (!alive) return;
      clearTimeout(setupTimer);
      registrationFailed = true;
      nativeActive = undefined;
      console.warn('[proactive] Native lifecycle listener could not be registered');
      changed('failure');
    });
  }
  return {
    native, isVisible,
    dispose() {
      alive = false;
      clearTimeout(setupTimer);
      document.removeEventListener('visibilitychange', returned);
      window.removeEventListener('focus', returned);
      window.removeEventListener('online', returned);
      if (listener) { void remove(listener); listener = undefined; }
    },
  };
}
